import React, { useState, useEffect } from 'react'
import { Save, X, Calendar, Flag, User, Tag, CheckSquare, Plus, Trash2 } from 'lucide-react'
import { useTasks, type Task } from '../../features/taskManagement'
import './TaskDetailWindow.scss'

interface TaskDetailWindowProps {
  taskId?: string
}

export const TaskDetailWindow: React.FC<TaskDetailWindowProps> = ({ taskId }) => {
  const { tasks, updateTask } = useTasks()
  const [task, setTask] = useState<Task | null>(null)
  const [editedTask, setEditedTask] = useState<Partial<Task>>({})
  const [isDirty, setIsDirty] = useState(false)
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')

  // Get window info if available
  const getWindowInfo = () => {
    if (typeof window !== 'undefined' && window.windowAPI) {
      return window.windowAPI.getCurrentWindowInfo()
    }
    return null
  }

  useEffect(() => {
    // Try to get taskId from window data or props
    let id = taskId
    const windowInfo = getWindowInfo()
    if (!id && windowInfo?.data?.taskId) {
      id = windowInfo.data.taskId
    }

    if (id) {
      const foundTask = tasks.find(t => t.id === id)
      if (foundTask) {
        setTask(foundTask)
        setEditedTask({ ...foundTask })
      }
    }
  }, [taskId, tasks])

  // Listen for window events
  useEffect(() => {
    if (!window.windowAPI) return

    const unsubscribeFocus = window.windowAPI.onWindowEvent('focus', () => {
    })

    const unsubscribeMessage = window.windowAPI.onWindowMessage('task:update', (data) => {
      if (data.taskId === task?.id) {
        setTask(data.task)
        setEditedTask(data.task)
      }
    })

    return () => {
      unsubscribeFocus()
      unsubscribeMessage()
    }
  }, [task])

  const handleFieldChange = (field: keyof Task, value: any) => {
    setEditedTask(prev => ({
      ...prev,
      [field]: value
    }))
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!task) return

    await updateTask(task.id, editedTask)
    setIsDirty(false)

    // Broadcast update to other windows
    if (window.windowAPI) {
      window.windowAPI.broadcast('task:updated', {
        taskId: task.id,
        task: { ...task, ...editedTask }
      })
    }
  }

  const handleCancel = () => {
    if (task) {
      setEditedTask({ ...task })
      setIsDirty(false)
    }
  }

  const handleClose = () => {
    if (isDirty) {
      const confirmed = window.confirm('Änderungen verwerfen?')
      if (!confirmed) return
    }

    if (window.windowAPI) {
      const windowInfo = getWindowInfo()
      if (windowInfo?.id) {
        window.windowAPI.closeWindow(windowInfo.id)
      }
    } else {
      window.close()
    }
  }

  const handleAddSubtask = () => {
    if (!newSubtaskTitle.trim()) return

    const newSubtask = {
      id: `subtask-${Date.now()}`,
      title: newSubtaskTitle,
      completed: false,
      order: (editedTask.subtasks?.length || 0) + 1
    }

    setEditedTask(prev => ({
      ...prev,
      subtasks: [...(prev.subtasks || []), newSubtask]
    }))

    setNewSubtaskTitle('')
    setIsDirty(true)
  }

  const handleRemoveSubtask = (subtaskId: string) => {
    setEditedTask(prev => ({
      ...prev,
      subtasks: prev.subtasks?.filter(st => st.id !== subtaskId)
    }))
    setIsDirty(true)
  }

  const handleToggleSubtask = (subtaskId: string) => {
    setEditedTask(prev => ({
      ...prev,
      subtasks: prev.subtasks?.map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      )
    }))
    setIsDirty(true)
  }

  const getAttentionColor = (attention: string) => {
    switch (attention) {
      case 'high': return 'task-detail__priority--high'
      case 'medium': return 'task-detail__priority--medium'
      case 'low': return 'task-detail__priority--low'
      default: return ''
    }
  }

  const getLifeSpaceColor = (lifeSpace: string) => {
    switch (lifeSpace) {
      case 'personal': return 'task-detail__life-space--personal'
      case 'work': return 'task-detail__life-space--work'
      case 'home': return 'task-detail__life-space--home'
      default: return ''
    }
  }

  if (!task) {
    return (
      <div className="task-detail task-detail--empty">
        <div className="task-detail__empty-state">
          <CheckSquare size={48} />
          <p>Keine Aufgabe ausgewählt</p>
        </div>
      </div>
    )
  }

  const completedSubtasks = editedTask.subtasks?.filter(st => st.completed).length || 0
  const totalSubtasks = editedTask.subtasks?.length || 0

  return (
    <div className="task-detail">
      <div className="task-detail__header">
        <div className="task-detail__header-left">
          <input
            type="checkbox"
            checked={editedTask.completed || false}
            onChange={(e) => handleFieldChange('completed', e.target.checked)}
            className="task-detail__checkbox"
          />
          <input
            type="text"
            value={editedTask.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            className="task-detail__title-input"
            placeholder="Aufgabentitel..."
          />
        </div>

        <div className="task-detail__header-actions">
          <button
            className="task-detail__save-btn"
            onClick={handleSave}
            disabled={!isDirty}
            title="Speichern"
          >
            <Save size={18} />
          </button>
          <button
            className="task-detail__close-btn"
            onClick={handleClose}
            title="Schließen"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="task-detail__content">
        {/* Description */}
        <div className="task-detail__section">
          <label className="task-detail__label">Beschreibung</label>
          <textarea
            value={editedTask.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            className="task-detail__textarea"
            placeholder="Beschreibung hinzufügen..."
            rows={4}
          />
        </div>

        {/* Meta Information */}
        <div className="task-detail__meta-grid">
          <div className="task-detail__meta-item">
            <Calendar size={16} />
            <label>Fällig am</label>
            <input
              type="date"
              value={editedTask.dueDate || ''}
              onChange={(e) => handleFieldChange('dueDate', e.target.value)}
              className="task-detail__date-input"
            />
          </div>

          <div className="task-detail__meta-item">
            <Flag size={16} />
            <label>Priorität</label>
            <select
              value={editedTask.attention || 'medium'}
              onChange={(e) => handleFieldChange('attention', e.target.value)}
              className={`task-detail__select ${getAttentionColor(editedTask.attention || 'medium')}`}
            >
              <option value="low">Niedrig</option>
              <option value="medium">Mittel</option>
              <option value="high">Hoch</option>
            </select>
          </div>

          <div className="task-detail__meta-item">
            <User size={16} />
            <label>Life Space</label>
            <select
              value={editedTask.lifeSpace || 'personal'}
              onChange={(e) => handleFieldChange('lifeSpace', e.target.value)}
              className={`task-detail__select ${getLifeSpaceColor(editedTask.lifeSpace || 'personal')}`}
            >
              <option value="personal">Persönlich</option>
              <option value="work">Arbeit</option>
              <option value="home">Zuhause</option>
              <option value="all">Alle</option>
            </select>
          </div>

          <div className="task-detail__meta-item">
            <Tag size={16} />
            <label>Kategorie</label>
            <input
              type="text"
              value={editedTask.category || ''}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              className="task-detail__input"
              placeholder="Kategorie..."
            />
          </div>
        </div>

        {/* Subtasks */}
        <div className="task-detail__section">
          <div className="task-detail__section-header">
            <CheckSquare size={16} />
            <span>Unteraufgaben</span>
            <span className="task-detail__progress">
              {completedSubtasks}/{totalSubtasks}
            </span>
          </div>

          {totalSubtasks > 0 && (
            <div className="task-detail__progress-bar">
              <div
                className="task-detail__progress-fill"
                style={{ width: `${(completedSubtasks / totalSubtasks) * 100}%` }}
              />
            </div>
          )}

          <div className="task-detail__subtasks">
            {editedTask.subtasks?.map(subtask => (
              <div key={subtask.id} className="task-detail__subtask">
                <input
                  type="checkbox"
                  checked={subtask.completed}
                  onChange={() => handleToggleSubtask(subtask.id)}
                  className="task-detail__subtask-checkbox"
                />
                <span className={subtask.completed ? 'task-detail__subtask--completed' : ''}>
                  {subtask.title}
                </span>
                <button
                  className="task-detail__subtask-remove"
                  onClick={() => handleRemoveSubtask(subtask.id)}
                  title="Entfernen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            <div className="task-detail__subtask-add">
              <Plus size={16} />
              <input
                type="text"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                placeholder="Neue Unteraufgabe hinzufügen..."
                className="task-detail__subtask-input"
              />
              <button
                onClick={handleAddSubtask}
                disabled={!newSubtaskTitle.trim()}
                className="task-detail__subtask-add-btn"
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="task-detail__section">
          <div className="task-detail__section-header">
            <Tag size={16} />
            <span>Tags</span>
          </div>
          <div className="task-detail__tags">
            {editedTask.tags?.map(tag => (
              <span key={tag} className="task-detail__tag">
                #{tag}
              </span>
            ))}
            <input
              type="text"
              placeholder="Tag hinzufügen..."
              className="task-detail__tag-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const input = e.target as HTMLInputElement
                  const newTag = input.value.trim()
                  if (newTag && !editedTask.tags?.includes(newTag)) {
                    handleFieldChange('tags', [...(editedTask.tags || []), newTag])
                    input.value = ''
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="task-detail__footer">
        <div className="task-detail__timestamps">
          <span>Erstellt: {new Date(task.createdAt).toLocaleString('de-DE')}</span>
          <span>Aktualisiert: {new Date(task.updatedAt).toLocaleString('de-DE')}</span>
        </div>

        <div className="task-detail__actions">
          {isDirty && (
            <button className="task-detail__btn task-detail__btn--secondary" onClick={handleCancel}>
              Verwerfen
            </button>
          )}
          <button
            className="task-detail__btn task-detail__btn--primary"
            onClick={handleSave}
            disabled={!isDirty}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  )
}